import React, { useMemo, useState } from 'react';
import {
  StyledAgeRestrictionLabelContainer,
  StyledProductDetailsAddToList,
  StyledProductDetailsEnergyLabel,
  StyledProductDetailsImageContainer,
  StyledProductDetailsImageWrapper,
  StyledProductDetailsLeft,
  StyledProductDetailsLeftHeader,
  StyledProductDetailsLeftHeaderWrapper,
  StyledProductDetailsPrice,
  StyledProductDetailsPriceSplash,
  StyledProductDetailsZCodes,
  StyledProductDetailsZCodesIcon,
  StyledProductImage,
} from '@organisms/ProductDetails/ProductDetailsLeftSection/ProductDetailsLeftSection.styles';
import Image from 'next/image';
import EnergyLabel from '@atoms/EnergyLabel/EnergyLabel';
import { isOfflineProduct, isOnlineProduct } from '@helpers/typeGuards/product';
import { getPdpAgeRestrictionImage, getValidProductLabels, isProductLabelKey } from '@helpers/productHelper';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import type { AxfoodOfflineProductViewModel, AxfoodProductDetailsViewModel } from '@occ/api-client';
import { useProductImage } from '@hooks/useProductImage';
import AddToListComponent from '@organisms/AddToListComponent/AddToListComponent';
import { makeSingleOfflineEntry, makeSingleOnlineEntry } from '@helpers/wishlistProductEntries';
import trackShoppingList from '@helpers/analyticsHelpers/trackShoppingList';
import Button from '@atoms/Button/Button';
import Icon from '@atoms/Icon/Icon';
import AddToList from '@icons/add-to-list.svg';
import { buildUrl } from '@helpers/buildUrl';

interface ProductDetailsLeftSectionProps {
  product: AxfoodOfflineProductViewModel | AxfoodProductDetailsViewModel;
  quantity: number | null;
  isPage?: boolean;
}

const ProductDetailsLeftSection = ({ product, quantity, isPage }: ProductDetailsLeftSectionProps) => {
  const t = useTranslations('product');
  const searchParams = useSearchParams();
  const ageRestrictionImage = useMemo(() => product && getPdpAgeRestrictionImage(product), [product]);
  const [addToListModalOpen, setAddToListModalOpen] = useState(false);

  const { productImageURL, productImageAltText, onImageLoadError } = useProductImage(product);

  return (
    <StyledProductDetailsLeft $isPage={isPage}>
      <StyledProductDetailsImageWrapper>
        <StyledProductDetailsImageContainer>
          <StyledProductImage
            priority
            unoptimized
            itemProp="image"
            src={productImageURL}
            alt={productImageAltText}
            width={isPage ? 416 : 320}
            height={isPage ? 416 : 320}
            data-testid="main-image"
            onError={onImageLoadError}
            $isPage={isPage}
          />

          {ageRestrictionImage && (
            <StyledAgeRestrictionLabelContainer>
              <Image
                priority
                unoptimized
                itemProp="image"
                src={`/icons/labels/${ageRestrictionImage}`}
                alt={t('label.age_restricted_18')}
                width={64}
                height={64}
              />
            </StyledAgeRestrictionLabelContainer>
          )}
        </StyledProductDetailsImageContainer>

        {product.energyDeclaration && (
          <StyledProductDetailsEnergyLabel>
            <EnergyLabel
              energyDeclaration={product.energyDeclaration}
              onClick={() => {
                if (isOnlineProduct(product) && typeof window !== 'undefined') {
                  window.history.pushState(null, '', buildUrl('', { tab: '3' }, searchParams));
                }
              }}
              size="large"
            />
          </StyledProductDetailsEnergyLabel>
        )}
      </StyledProductDetailsImageWrapper>
      <StyledProductDetailsLeftHeader>
        <StyledProductDetailsLeftHeaderWrapper>
          <div data-testid="left-labels">
            <StyledProductDetailsAddToList>
              <Button
                variant="transparent"
                onClick={() => setAddToListModalOpen(true)}
                title={t('addToListLabel')}
                aria-label={t('addToListLabel')}
              >
                <Icon svg={AddToList} size={20} />
              </Button>
              <AddToListComponent
                product={product as AxfoodProductDetailsViewModel}
                productEntries={
                  isOfflineProduct(product)
                    ? [makeSingleOfflineEntry(product, quantity)]
                    : [makeSingleOnlineEntry(product, quantity)]
                }
                tracking={{ parent: 'product_detail' }}
                addToListCallback={() => {
                  trackShoppingList({ action: 'current', parent: 'product_detail' });
                }}
                createNewListCallback={() => {
                  trackShoppingList({ action: 'new', parent: 'product_detail' });
                }}
                key={`add-to-list-${isOnlineProduct(product) ? product?.code : product?.potentialPromotions?.[0]?.code}-${product.name}`}
                onClose={() => setAddToListModalOpen(false)}
                isOpen={addToListModalOpen}
              />
            </StyledProductDetailsAddToList>
            {(product?.labels?.length || 0) > 0 && (
              <StyledProductDetailsZCodes data-testid="zcodes-icons">
                {getValidProductLabels(product, 3)
                  .filter((label) => label !== 'age_restricted_18')
                  .map((label) => {
                    return (
                      <StyledProductDetailsZCodesIcon
                        src={`/icons/labels/${label}.svg`}
                        key={label}
                        title={isProductLabelKey(label) ? t(`label.${label}`) : label}
                      />
                    );
                  })}
              </StyledProductDetailsZCodes>
            )}
          </div>

          <StyledProductDetailsPrice $isPage={isPage}>
            <StyledProductDetailsPriceSplash product={product} showSavePrice="extended" />
          </StyledProductDetailsPrice>
        </StyledProductDetailsLeftHeaderWrapper>
      </StyledProductDetailsLeftHeader>
    </StyledProductDetailsLeft>
  );
};

export default ProductDetailsLeftSection;
